import React, { useState, useRef, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { useTranslation } from '../lib/LanguageContext';
import { supabase } from '../lib/supabase';
import { isWithinAILimit, incrementAIUsage } from '../lib/aiUsage';
import { htmlToMd } from '../lib/htmlToMd';

interface NotesAIAssistantProps {
  existingContent: string;
  onResult: (mdContent: string) => void;
}

export function NotesAIAssistant({ existingContent, onResult }: NotesAIAssistantProps) {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiLimitReached, setAiLimitReached] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (error || aiLimitReached) {
      const timer = setTimeout(() => {
        setError(null);
        setAiLimitReached(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, aiLimitReached]);

  const generateContent = async () => {
    if (!prompt.trim()) return;

    setLoading(true);
    setError(null);
    setAiLimitReached(false);

    try {
      const isWithinLimit = await isWithinAILimit();
      if (!isWithinLimit) {
        setAiLimitReached(true);
        setError(t('aiDailyLimitReached'));
        setLoading(false);
        return;
      }

      const { data, error: functionError } = await supabase.functions.invoke('generate-notes-content', {
        body: { prompt: prompt.trim(), existingContent }
      });

      if (functionError) throw new Error(`Edge function error: ${functionError.message}`);

      if (data && data.content && typeof data.content === 'string') {
        // The edge function returns Markdown; htmlToMd passes it through and
        // still converts HTML from an older deployment. Replaces the whole note
        // (the edge function returns the full, updated note)
        onResult(htmlToMd(data.content));
        await incrementAIUsage();
        window.dispatchEvent(new CustomEvent('aiUsageUpdated'));
        setPrompt('');
      } else {
        throw new Error(t('aiInvalidResponse'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('aiGenerateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      generateContent();
    }
  };

  return (
    <div className="px-4 py-3 border-b bg-gray-50">
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('aiNotePlaceholder')}
          className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
          disabled={loading}
        />
        <button
          onClick={generateContent}
          disabled={loading || !prompt.trim()}
          className="flex items-center justify-center p-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title={t('aiGenerateContent')}
        >
          <Sparkles className={`h-5 w-5 ${loading ? 'animate-pulse' : ''}`} />
        </button>
      </div>

      {(error || aiLimitReached) && (
        <div className="mt-2 text-sm">
          {aiLimitReached ? (
            <div className="text-red-600">
              <p className="font-medium">Daily AI limit reached.</p>
              <p className="text-xs mt-1">Upgrade to premium for more AI suggestions.</p>
            </div>
          ) : error ? (
            <p className="text-red-600">{error}</p>
          ) : null}
        </div>
      )}

      {loading && (
        <div className="mt-2 text-sm text-gray-600">Generating content...</div>
      )}
    </div>
  );
}
